import React from 'react'
import CustomModal from '../../baseComponents/gui/customModal/CustomModal'
import CustomButton from '../../features/customButton/ui/main/CustomButton'
import { useModal } from '../../../hooks/useModal'
import { safeHTML } from '../../../utils/safeHTML'
import Reward from '../../baseComponents/gui/reward/Reward'
import { useDispatch } from 'react-redux'
import user, { useUser } from '../../../redux/reducer/user'
import { PREMIUM, STANDARD, useAccountMode } from '../../../hooks/useAccountMode'
import { ClothReward } from '../../features/clothReward/ClothReward'
import { canGetPremiumReward } from '../../../utils/helpers'
import { useTranslation } from '../../../translations'

import { useFeatureToggle } from '../../../hooks/useFeatureToggle'
import { useApplicationCallbacks } from '../../../providers/application-callbacks'

const TaskCompleteModal = React.forwardRef(function TaskCompleteModal(
  {
    title,
    premiumNote,
    attempts,
    price,
    score,
    buttonContinue,
    charms,
    onClose,
    isFreeAttempts,
    premiumPrize,
    ticket,
    gacha_item_count,
    clothes,
    gachaItemType,
    gachaItem,
    prizeImage,
    beauties,
    rewardBonuses,
  },
  ref,
) {
  const callbacks = useApplicationCallbacks()
  const dispatch = useDispatch()
  const { profile } = useUser()
  const { id, closeModal } = useModal()
  const { mode: accountMode } = useAccountMode()
  const { t } = useTranslation()

  const gameTamagotchiBeauties = useFeatureToggle('gameTamagotchiBeauties')

  const {
    money: premiumPrice,
    attempts: premiumAttempts,
    gacha_event_cash: premiumGacha,
  } = premiumPrize ?? {}

  const rewardMode = { [PREMIUM]: premiumPrize ? PREMIUM : STANDARD, [STANDARD]: STANDARD }[
    accountMode
  ]

  const hasStandardReward =
    price ||
    attempts ||
    charms ||
    ticket ||
    beauties ||
    clothes?.length ||
    rewardBonuses ||
    (score && !rewardBonuses)

  const showGachaReward =
    !hasStandardReward && !rewardBonuses && (gacha_item_count || gachaItem || prizeImage)

  const rewardImage = prizeImage && !showGachaReward ? prizeImage : null

  const close = () => {
    onClose?.()

    if (isFreeAttempts)
      dispatch(
        user.actions.change({
          key: 'profile',
          value: { ...profile, newFreeAttempts: 0 },
        }),
      )

    if (gameTamagotchiBeauties) {
      callbacks.getBeautiesBalance()
    }

    closeModal({ id })
  }

  const prizes = []

  if (price)
    prizes.push({
      key: 'coin',
      node: (
        <Reward
          type={'coin'}
          value={price}
          mod={rewardMode}
          src={rewardImage}
        />
      ),
    })
  if (attempts)
    prizes.push({
      key: 'energy',
      node: (
        <Reward
          type={'energy'}
          value={attempts}
          mod={rewardMode}
          src={rewardImage}
        />
      ),
    })
  if (charms)
    prizes.push({
      key: 'charm',
      node: (
        <Reward
          type={'charm'}
          value={charms}
          mod={rewardMode}
          src={rewardImage}
        />
      ),
    })
  if (ticket)
    prizes.push({
      key: 'ticket',
      node: (
        <Reward
          type={'ticket'}
          value={ticket}
          mod={rewardMode}
          src={rewardImage}
        />
      ),
    })
  if (showGachaReward)
    prizes.push({
      key: 'gacha',
      node: (
        <Reward
          type={gachaItemType || 'gachaItem'}
          value={gacha_item_count ?? 1}
          mod={rewardMode}
          src={prizeImage}
        />
      ),
    })
  if (clothes) prizes.push({ key: 'clothes', node: <ClothReward clothes={clothes} /> })
  if (beauties)
    prizes.push({
      key: 'beauties',
      node: (
        <Reward
          type={'beauties'}
          value={beauties}
          mod={rewardMode}
          src={rewardImage}
        />
      ),
    })
  if (rewardBonuses)
    prizes.push({
      key: 'reward-bonuses',
      node: (
        <Reward
          type={'bonusesFire'}
          value={rewardBonuses}
          mod={rewardMode}
          src={prizeImage}
        />
      ),
    })
  if (score && !rewardBonuses)
    prizes.push({
      key: 'score',
      node: (
        <Reward
          type={'xp'}
          value={score}
          mod={rewardMode}
          src={rewardImage}
        />
      ),
    })

  return (
    <CustomModal>
      <div
        className={'task-complete-modal'}
        ref={ref}
      >
        <div
          className={'task-complete-modal__bg'}
          onClick={close}
        />
        <div className={'task-complete-modal__content'}>
          <div className={'task-complete-modal__prizes'}>
            {!!prizes.length && (
              <div className={'task-complete-modal__prizes-main'}>
                {prizes.map(({ key, node }, index) => (
                  <React.Fragment key={key}>
                    {!!index && <div className={'task-complete-modal__prizes-del'} />}
                    {node}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>

          <div className={'task-complete-modal__title'}>{safeHTML(t(title))}</div>

          {premiumPrize && (
            <div className={'task-complete-modal__energy-text'}>
              {safeHTML(
                {
                  [PREMIUM]: t(premiumNote[accountMode]),
                  [STANDARD]: canGetPremiumReward(
                    t(premiumNote[accountMode]),
                    premiumPrice,
                    premiumAttempts,
                    premiumGacha,
                    t,
                  ),
                }[accountMode],
              )}
            </div>
          )}

          <CustomButton
            {...buttonContinue}
            onClick={close}
          />
        </div>
      </div>
    </CustomModal>
  )
})

export default TaskCompleteModal
TaskCompleteModal.propTypes = {}
